import styles from "./MovieCard.module.scss";
import LikeButton from "../../../components/shared/LikeButton";
import SavedIcon from "../../../components/icons/searchIcons/savedIcon";
import { Movie } from "./MoviesList";
import { genresData } from "../../../constants/structure";
import { setCurrentItem } from "../../../store/slices/currentItem.slice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants/routes";
import { useTranslation } from "react-i18next";
import OverlayRatingIcon from "../../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../../components/icons/searchIcons/OverlayTomatoRatingIcon";
import { formatLikeCount, movieTitleHandler, posterPathHandler } from "../../../utils/helpers";
import { setLoginModal } from "../../../store/slices/modals.slice";
import { useEffect, useState } from "react";

interface ISimilarContentCardProps {
  isMobile: boolean;
  item: Movie;
  liked: boolean;
  isFav: boolean;
  likeHandler: (e: any, item: any, liked: boolean) => void;
  updateVoteLocal: (id: any, key: string, type: string) => void;
  favouritesHandler: (e: any, item: any, isFav: boolean) => void;
}

const formatRating = (rating?: number | null) => (rating != null ? rating.toFixed(1) : null);

const MovieCard = ({
  isMobile,
  item,
  liked,
  likeHandler,
  isFav,
  favouritesHandler,
  updateVoteLocal,
}: ISimilarContentCardProps) => {
  const [localImg, setLocalImg] = useState("");
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [fav, setFav] = useState(isFav);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    setFav(isFav);
  }, [isFav]);
  const title = movieTitleHandler(i18n.language, item);

  useEffect(() => {
    const path = posterPathHandler(i18n.language, item);
    if (path) {
      setImgLoaded(false);
      setLocalImg(`${process.env.REACT_APP_IMG_BASE_URL}${path}`);
      setImgError(false);
    } else {
      setImgError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  const dirData = i18n.language === "ar" ? "rtl" : "ltr";
  const imdbRating = formatRating(item.imdb_rating);
  const tomatoRating = formatRating(item.rotten_tomatoes_rating);

  return (
    <div
      dir={dirData}
      id={item.id.toString()}
      className={styles.cardWrapper}
      onClick={() => {
        dispatch(setCurrentItem(item));
        navigate(`${ROUTES.DETAILS}/${item?.id}`);
      }}
    >
      <div className={styles.imgWrapper}>
        {!imgError && (
          <img
            width={119}
            height={166}
            className={styles.image}
            src={localImg}
            style={{ opacity: imgLoaded ? 1 : 0 }}
            onLoad={() => setImgLoaded(true)}
            onError={() => {
              const fallback = posterPathHandler("en", item);
              if (fallback && !localImg.includes(fallback)) {
                setImgLoaded(false);
                setLocalImg(`${process.env.REACT_APP_IMG_BASE_URL}${fallback}`);
              } else {
                setImgError(true);
              }
            }}
            alt="#"
          />
        )}
        {(imgError || !imgLoaded) && (
          <div
            className={styles.image}
            style={{
              width: 119,
              height: 166,
              borderRadius: 8,
              position: imgError ? "static" : "absolute",
              top: 0,
              left: 0,
            }}
          />
        )}
      </div>
      <div className={styles.textWrapper}>
        <div
          dir={dirData}
          className={styles.savedIconWrapper}
          onClick={(e: any) => {
            const token = localStorage.getItem("accessToken");
            if (!token) {
              dispatch(setLoginModal(true));
              return;
            }

            favouritesHandler(e, item, isFav);
          }}
        >
          <SavedIcon className={`${isMobile ? styles.savedIcon : styles.savedIconDesctop} `} inFavourites={fav} />
        </div>
        <div className={styles.titleContent}>
          <h4 className={styles.movieTitle}>{title}</h4>
          <span className={styles.titleTooltip}>{title}</span>
        </div>
        <div className={styles.infoWrapper}>
          <div className={styles.rowInfoWrapper}>
            <div className={styles.badge}>{item.release_date?.substring(0, 4)}</div>
          </div>
          <p className={styles.description}>
            {item.genre_ids[0] && (
              <span>{t(`${genresData.find((genre) => genre.id === item.genre_ids[0])?.name}`) || "Action"}</span>
            )}
            {item.genre_ids[1] && (
              <span>{t(`${genresData.find((genre) => genre.id === item.genre_ids[1])?.name}`) || "Action"}</span>
            )}
          </p>

          <div className={styles.cardActionsWrapper}>
            {imdbRating && (
              <div className={styles.ratingItem}>
                <OverlayRatingIcon />
                <p className={styles.rating}>{imdbRating}</p>
              </div>
            )}
            {tomatoRating && (
              <div className={styles.ratingItem}>
                <OverlayTomatoRatingIcon />
                <p className={styles.rating}>{tomatoRating}</p>
              </div>
            )}
            <LikeButton
              clickHandler={likeHandler}
              direction="row"
              isMobile={isMobile}
              className={styles.likeButton}
              count={formatLikeCount(item.vote_count, { compact: true })}
              updateFieldLocal={updateVoteLocal}
              liked={liked}
              item={item}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovieCard;
